<template>
  <div class="main-content">
    <div class="back-button-container" @click="goTo(routes.ticketSeller.reports)">
      <div class="back-button-icon-container">
        <img class="back-button-icon" src="/assets/back-icon.svg" :alt="adminText('workflowsAll.back_button_4e9e2cc', 'Back Button')">
      </div>
      <div class="back-button-text-container">{{ adminText('workflowsAll.back_to_reports_f709622', 'Back to reports') }}</div>
    </div>
    <div class="report-content">
      <div class="report-title">
        {{ reportTitle }}
      </div>
      <div v-if="selectedReportType !== ReportTypes.CUSTOM" class="report-radio-buttons-container">
        <div class="report-radio-buttons-title">
          {{ adminText('workflowsAll.select_a_e103f76', 'Select a') }}
          {{ workflowLabel(reportLabels.subtitle) }}
          {{ adminText('workflowsAll.you_want_to_generate_a_report_for_bc47693', 'you want to generate a report for:') }}
        </div>
        <div class="report-radio-buttons">
          <label
            :class="
              checkedReportOption === 1
                ? 'radio-button-container checked'
                : 'radio-button-container'
            "
          >
            {{ workflowLabel(reportLabels.firstRadio) }}
            <input
              type="radio"
              checked="checked"
              name="radio"
              value="1"
              @change="changeCheckedOption(1)"
            >
            <span class="checkmark" />
          </label>
          <label
            :class="
              checkedReportOption === 2
                ? 'radio-button-container checked'
                : 'radio-button-container'
            "
          >
            {{ workflowLabel(reportLabels.secondRadio) }}
            <input type="radio" name="radio" value="2" @change="changeCheckedOption(2)">
            <span class="checkmark" />
          </label>
          <label
            :class="
              checkedReportOption === 3
                ? 'radio-button-container checked'
                : 'radio-button-container'
            "
          >
            {{ workflowLabel(reportLabels.customRadio) }}
            <input type="radio" name="radio" value="3" @change="changeCheckedOption(3)">
            <span class="checkmark" />
          </label>
        </div>
      </div>
      <div v-if="selectedReportType !== ReportTypes.CUSTOM" class="report-date-picker">
        <v-dialog v-model="display" :disabled="checkedReportOption !== 3" width="320" full-width>
          <v-text-field
            slot="activator"
            class="report-date-field"
            :disabled="checkedReportOption !== 3"
            :label="reportDateLabel"
            :value="determineTextFieldString()"
            readonly
            box
          />
          <v-date-picker
            ref="picker"
            :locale="isAdminLocalization ? $adminLocale() : undefined"
            :value="selectedDate ? selectedDate.toISOString().substr(0, 10) : ''"
            :max="new Date().toISOString().substr(0, 10)"
            full-width
            scrollable
            :reactive="selectedReportType === ReportTypes.YEARLY"
            :type="
              [ReportTypes.DAILY, ReportTypes.CASH_REGISTER].includes(selectedReportType)
                ? 'date'
                : 'month'
            "
            @input="changeSelectedDate('selectedDate', $event)"
          />
        </v-dialog>
      </div>
      <div v-else class="report-date-pickers-container">
        <div class="report-date-picker">
          <div class="report-radio-buttons-title custom">
            {{ adminText('workflowsAll.select_an_interval_you_want_to_generate_a_rep_79b81b0', 'Select an interval you want to generate a report for:') }}
          </div>
          <div class="report-picker-custom-title">{{ adminText('workflowsAll.from_c4d63e4', 'From:') }}</div>
          <v-dialog v-model="displayCustomFrom" width="320" full-width>
            <v-text-field
              slot="activator"
              class="report-date-field custom"
              :label="adminText('workflowsAll.select_a_date_642b09c', 'Select a date')"
              :value="selectedFrom ? selectedFrom.toLocaleDateString() : ''"
              readonly
              box
            />
            <v-date-picker
              :locale="isAdminLocalization ? $adminLocale() : undefined"
              :value="selectedFrom ? selectedFrom.toISOString().substr(0, 10) : ''"
              :max="selectedTo.toISOString().substr(0, 10)"
              full-width
              scrollable
              @input="changeSelectedDate('selectedFrom', $event)"
            />
          </v-dialog>
        </div>
        <div class="report-date-picker">
          <div class="report-picker-custom-title">{{ adminText('workflowsAll.to_7e7fbc8', 'To:') }}</div>
          <v-dialog v-model="displayCustomTo" width="320" full-width>
            <v-text-field
              slot="activator"
              class="report-date-field custom"
              :label="adminText('workflowsAll.select_a_date_642b09c', 'Select a date')"
              :value="selectedTo ? selectedTo.toLocaleDateString() : ''"
              readonly
              box
            />
            <v-date-picker
              :locale="isAdminLocalization ? $adminLocale() : undefined"
              :value="selectedTo ? selectedTo.toISOString().substr(0, 10) : ''"
              :max="new Date().toISOString().substr(0, 10)"
              :min="selectedFrom.toISOString().substr(0, 10)"
              full-width
              scrollable
              @input="changeSelectedDate('selectedTo', $event)"
            />
          </v-dialog>
        </div>
      </div>
      <div class="report-button-container">
        <button
          :disabled="
            selectedReportType === ReportTypes.CUSTOM ? !selectedTo || !selectedFrom : !selectedDate
          "
          class="report-button"
          type="button"
          @click="handleGeneratingReportPdf"
        >
          {{ adminText('workflowsAll.generate_569e9e1', 'GENERATE') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { ReportTypes } from '@/types'
import { frontendURL } from '@/utils'
import moment from 'moment'

const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD'

export default {
  mixins: [WorkflowDisplay],
  props: {
    selectedReportType: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      ReportTypes,
      checkedReportOption: 1,
      reportLabels: {
        subtitle: '',
        firstRadio: '',
        secondRadio: '',
        customRadio: '',
      },
      selectedDate: new Date(),
      selectedFrom: new Date(),
      selectedTo: new Date(),
      display: false,
      displayCustomFrom: false,
      displayCustomTo: false,
    }
  },
  computed: {
    reportTitle() {
      switch (this.selectedReportType) {
      case this.ReportTypes.DAILY:
        return this.adminText('workflowsAll.daily_sales_report_8c52942', 'Daily Sales Report')
      case this.ReportTypes.MONTHLY:
        return this.adminText('workflowsAll.monthly_sales_report_9a40f09', 'Monthly Sales Report')
      case this.ReportTypes.YEARLY:
        return this.adminText('workflowsAll.yearly_sales_report_82259bb', 'Yearly Sales Report')
      case this.ReportTypes.CUSTOM:
        return this.adminText('workflowsAll.custom_sales_report_510eef6', 'Custom Sales Report')
      case this.ReportTypes.CASH_REGISTER:
        return this.adminText('workflowsAll.cash_register_report_dfee8e4', 'Cash register report')
      default:
        return this.selectedReportType
      }
    },
    reportDateLabel() {
      switch (this.selectedReportType) {
      case this.ReportTypes.DAILY:
        return this.adminText('workflowsAll.select_a_date_642b09c', 'Select a date')
      case this.ReportTypes.MONTHLY:
        return this.adminText('workflowsAll.select_a_month_474efd2', 'Select a month')
      case this.ReportTypes.YEARLY:
        return this.adminText('workflowsAll.select_a_year_5d9a021', 'Select a year')
      case this.ReportTypes.CASH_REGISTER:
        return this.adminText('workflowsAll.select_a_date_642b09c', 'Select a date')
      default:
        return ''
      }
    },
  },
  watch: {
    display() {
      this.$refs.picker.activePicker =
        this.selectedReportType === ReportTypes.YEARLY
          ? 'YEAR'
          : this.selectedReportType === ReportTypes.MONTHLY
            ? 'MONTH'
            : 'DATE'
    },
  },
  beforeMount() {
    if (!this.selectedReportType) {
      this.goTo(this.routes.ticketSeller.reports)
    }

    switch (this.selectedReportType) {
    case ReportTypes.DAILY:
    case ReportTypes.CASH_REGISTER:
      this.reportLabels.subtitle = 'workflowsAll.date_e1a56a6'
      this.reportLabels.firstRadio = 'workflowsAll.today_470358f'
      this.reportLabels.secondRadio = 'workflowsAll.yesterday_35aa466'
      this.reportLabels.customRadio = 'workflowsAll.other_date_ddb46c5'
      break
    case ReportTypes.MONTHLY:
      this.reportLabels.subtitle = 'workflowsAll.month_ce7005f'
      this.reportLabels.firstRadio = 'workflowsAll.this_month_3e2ca9c'
      this.reportLabels.secondRadio = 'workflowsAll.last_month_5b5de11'
      this.reportLabels.customRadio = 'workflowsAll.other_month_6d9979a'
      break
    case ReportTypes.YEARLY:
      this.reportLabels.subtitle = 'workflowsAll.year_adc467d'
      this.reportLabels.firstRadio = 'workflowsAll.this_year_683a2d9'
      this.reportLabels.secondRadio = 'workflowsAll.last_year_5f6fc8f'
      this.reportLabels.customRadio = 'workflowsAll.other_year_6611b32'
      break
    }
  },
  methods: {
    changeCheckedOption(value) {
      this.checkedReportOption = value
      if (value === 1) {
        this.selectedDate = new Date()
      } else if (value === 2) {
        this.selectedDate = new Date()
        if (this.selectedReportType === ReportTypes.YEARLY) {
          this.selectedDate.setYear(this.selectedDate.getFullYear() - 1)
        } else if (this.selectedReportType === ReportTypes.MONTHLY) {
          this.selectedDate.setMonth(this.selectedDate.getMonth() - 1)
        } else {
          this.selectedDate.setDate(this.selectedDate.getDate() - 1)
        }
      } else {
        this.selectedDate = null
      }
    },
    changeSelectedDate(picker, val) {
      let date = moment(val, DEFAULT_DATE_FORMAT)
      let input = moment().year(date.year()).month(date.month()).date(date.date())
      this[picker] = input.toDate()
      this.display = false
      this.displayCustomFrom = false
      this.displayCustomTo = false
    },
    determineTextFieldString() {
      if (!this.selectedDate) {
        return ''
      }

      switch (this.selectedReportType) {
      case ReportTypes.DAILY:
      case ReportTypes.CASH_REGISTER:
        return this.selectedDate.toLocaleDateString(this.isAdminLocalization ? this.$adminLocale() : undefined)
      case ReportTypes.MONTHLY:
        return `${this.selectedDate.toLocaleString(this.isAdminLocalization ? this.$adminLocale() : 'default', {
          month: 'long',
        })} ${this.selectedDate.getFullYear()}`
      case ReportTypes.YEARLY:
        return this.selectedDate.getFullYear()
      }
    },
    handleGeneratingReportPdf() {
      if (this.selectedReportType === ReportTypes.CUSTOM) {
        this.selectedFrom.setHours(0, 0, 0, 0)
        this.selectedTo.setHours(23, 59, 59, 59)

        window.open(
          `${frontendURL}/#/${
            this.routes.ticketSeller.reportPdf
          }?from=${this.selectedFrom.toISOString()}&to=${this.selectedTo.toISOString()}`,
        )
      } else {
        const from = moment(this.selectedDate).startOf('day')
        const to = moment(this.selectedDate).endOf('day')

        if (this.selectedReportType === ReportTypes.YEARLY) {
          from.startOf('year')
          to.endOf('year')
        } else if (this.selectedReportType === ReportTypes.MONTHLY) {
          from.startOf('month')
          to.endOf('month')
        }

        const reportRoute =
          this.selectedReportType === ReportTypes.CASH_REGISTER
            ? this.routes.ticketSeller.cashRegisterStatePdf
            : this.routes.ticketSeller.reportPdf

        window.open(
          `${frontendURL}/#/${reportRoute}?from=${from.toISOString()}&to=${to.toISOString()}`,
        )
      }
    },
  },
}
</script>

<style scoped>
.main-content {
  max-width: 676px;
}

.back-button-container {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  cursor: pointer;
  width: fit-content;
}

@media only screen and (max-width: 800px) {
  .back-button-container {
    margin-top: 0.5rem;
    margin-bottom: 0;
  }
}

.back-button-icon-container {
  margin-right: 1rem;
  display: flex;
  align-items: center;
}

.back-button-icon {
  height: 1rem;
}

.back-button-text-container {
  font-family: 'Raleway', sans-serif;
  font-size: 1.25rem;
  font-weight: bold;
  color: #ff4c00;
  line-height: 120%;
  letter-spacing: -0.015em;
}

.report-content {
  background-color: #f5f5f5;
  padding: 2.5rem 3rem;
  margin-top: 1rem;
  border-radius: 2rem;
}

.report-title {
  font-family: 'Raleway', sans-serif;
  font-style: normal;
  font-weight: 800;
  font-size: 2.25rem;
  line-height: 120%;
  letter-spacing: -0.03em;
  color: #0277bd;
  text-transform: capitalize;
}

.report-radio-buttons-container {
  margin-top: 1.5rem;
}

.report-radio-buttons-title {
  font-family: 'Source Sans Pro', sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 0.875rem;
  line-height: 120%;
  color: #979797;
  margin-bottom: 1rem;
}

.report-radio-buttons {
  display: flex;
}

.radio-button-container {
  display: flex;
  height: fit-content;
  position: relative;
  padding: 0.5rem 1rem 0.5rem 2.875rem;
  border: 2px solid #666666;
  border-radius: 1.5rem;
  cursor: pointer;
  font-family: 'Raleway', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 1rem;
  line-height: 120%;
  letter-spacing: -0.015em;
  font-feature-settings: 'pnum' on, 'onum' on;
  color: #666666;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.radio-button-container:not(:first-child) {
  margin-left: 1rem;
}

.radio-button-container.checked {
  background-color: #ff4c00;
  color: white;
  border-color: #ff4c00;
}

.radio-button-container.checked:hover {
  background-color: #ff4c00;
}

.radio-button-container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 1rem;
  height: 1.125rem;
  width: 1.125rem;
  background-color: transparent;
  border-radius: 50%;
  border: 2px solid #666666;
}

.radio-button-container:hover input ~ .checkmark,
.radio-button-container:hover {
  background-color: rgb(231, 231, 231);
}

.radio-button-container input:checked ~ .checkmark {
  background-color: #ff4c00;
  border: 2px solid white;
}

.checkmark:after {
  content: '';
  position: absolute;
  display: none;
}

.radio-button-container input:checked ~ .checkmark:after {
  display: block;
  box-sizing: border-box;
}

.radio-button-container .checkmark:after {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: white;
}

.report-date-field {
  margin-top: 2rem;
}

.report-date-field >>> .v-input__slot {
  background-color: white !important;
}

.report-date-pickers-container {
  margin-top: 1.5rem;
}

.report-date-field.custom {
  margin-top: 0;
}

.report-radio-buttons-title.custom {
  margin-bottom: 0.5rem;
}

.report-picker-custom-title {
  margin-bottom: 0.5rem;
  font-family: 'Raleway', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 1rem;
  line-height: 120%;
  letter-spacing: -0.015em;
  font-feature-settings: 'pnum' on, 'onum' on;
  color: #666666;
}

.report-button-container {
  margin-top: 2.5rem;
}

.report-button {
  padding: 0.5rem 1rem;
  display: flex;
  width: fit-content;
  font-family: 'Raleway', sans-serif;
  font-style: normal;
  font-weight: bold;
  font-size: 1.125rem;
  line-height: 120%;
  letter-spacing: -0.015em;
  text-transform: uppercase;
  color: #ffffff;
  background-color: #0277bd;
  border-radius: 19px;
}

.report-button:disabled {
  background-color: #979797;
}
</style>
